import { Typography, Stack, Box } from '@mui/material';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import DOMPurify from 'dompurify';

import { ReactComponent as Regenerate } from 'common/assets/images/ai-generate.svg';
import { ReactComponent as SaveIcon } from 'common/assets/images/save-Icon.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import PromptSelect from 'common/components/PromptSelect/PromptSelect';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	AI_SIMULATION_MODEL_KEYS,
	LOCATION_OPTIONS,
} from 'common/constants/AISimulationConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setIsAISummaryRegenerated } from 'common/store/reducer/horizonScanReducer';

import {
	generateAISimulationPromptResult,
	generateAISimulationResult,
} from 'features/AiSimulation/api';
import { HORIZON_PROGRESS_BAR_ELEMENTS } from 'features/HorizonScanning/HorizonScanConstants';

import { updateAISumamry } from '../../api/index';
import styles from '../../HorizonDetailLayout/HorizonDetailLayout.module.scss';

import {
	HTTP_STATUS_CODES,
	Loader,
} from '../ActionPlanLayout/ActionPlanTaskTableModules';

import aisummaryStyles from './AISummary.module.scss';

const { NOTSTARTED, COMPLETE, IMPACT_ANALYSIS, APPLICABILITY_ASSESSMENT } =
	HORIZON_PROGRESS_BAR_ELEMENTS;

const AISummary = (props: any) => {
	const { data, subPartId, isLoading } = props;
	const [isEditing, setIsEditing] = useState(false);
	const { HORIZON_SCANNING: HORIZON } = LOCATION_OPTIONS;
	const { activeIconColorPrimary, disabledIconColor } = aisummaryStyles;
	const [editData, setEditData] = useState(data?.aiSummary);
	const [textData, setTextData] = useState(data?.aiSummary);
	const [noChange, setNoChange] = useState(true);
	const dispatch = useDispatch();
	const containerRef = useRef<HTMLDivElement>(null);
	const [isRegenerateLoading, setIsRegenerateLoading] = useState(false);
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [isSaved, setIsSaved] = useState(false);

	const { OK } = HTTP_STATUS_CODES;
	const { HORIZON_SCANNING_KEY } = AI_SIMULATION_MODEL_KEYS;

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const currentWorkflowStatus = useSelector(
		(state: RootState) => state.horizonScan.currentWorkflowStatus,
	);

	const loggedInUser: any = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const isRegenerateVisible =
		currentWorkflowStatus === APPLICABILITY_ASSESSMENT ||
		currentWorkflowStatus === IMPACT_ANALYSIS;

	useEffect(() => {
		setTextData(data?.aiSummary);
		setEditData(data?.aiSummary);
	}, [data]);

	const shouldDisableEdit = () => {
		if (
			!data?.assignedUserId ||
			currentWorkflowStatus === NOTSTARTED ||
			currentWorkflowStatus === COMPLETE ||
			data?.assignedUserId !== loggedInUser?.userId
		) {
			return true;
		}
		return false;
	};

	const handleSaveClick = async () => {
		const res = await updateAISumamry(subPartId, editData);
		if (res?.statusCode === OK) {
			setTextData(res.responseBody?.aiSummary);
			setIsSaved(true);
			dispatch(
				setToastMessage({
					message: 'Details saved successfully',
					severity: 'success',
					isToastOpen: true,
				}),
			);
		} else if (res?.errorBody || res?.error) {
			const { errorBody } = res;
			const responseError: any = res.error;
			const errorMessage =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsEditing(false);
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const handleTextClick = () => {
		if (!shouldDisableEdit()) {
			setIsEditing(true);
		}
	};

	const generatePromptSelection = async (event: any) => {
		const promptKey = event.key;
		regenerateButtonClicked(promptKey);
	};

	const regenerateClicked = async () => {
		regenerateButtonClicked('');
	};

	const regenerateButtonClicked = async (promptKey: string) => {
		setIsRegenerateLoading(true);
		const text = data.informationText;
		const response = promptKey.length
			? await generateAISimulationPromptResult(promptKey, text)
			: await generateAISimulationResult(HORIZON_SCANNING_KEY, text, HORIZON);
		if (response?.statusCode === OK) {
			dispatch(setIsAISummaryRegenerated(true));
			const newSummary = response.responseBody?.answer;
			setTextData(newSummary);
			setEditData(newSummary);
			setIsRegenerating(true);
			setNoChange(false);
			setIsSaved(false);
		} else if (response?.errorBody || response?.error) {
			const { errorBody } = response;
			const responseError: any = response.error;
			const errorMessage =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsRegenerateLoading(false);
	};

	const handleClickOutside = (event: MouseEvent) => {
		if (
			containerRef.current &&
			!containerRef.current.contains(event.target as Node)
		) {
			setIsEditing(false);
		}
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	let AISummaryContent;
	if (isRegenerateLoading) {
		AISummaryContent = (
			<Loader
				show={isRegenerateLoading}
				className={aisummaryStyles['loader-styling-regenerate']}
			/>
		);
	} else if (isEditing) {
		AISummaryContent = (
			<TextFieldWithLabel
				inputProps={{
					'data-testid': 'summary-edit-area',
					className: aisummaryStyles['summary-textarea'],
				}}
				id="summary-edit-area"
				value={editData}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
					setEditData(event.target.value);
					setNoChange(false);
				}}
				multiLine
				showLabel={false}
				error={false}
				helperText=""
				className={aisummaryStyles['summary-edit-area']}
			/>
		);
	} else {
		AISummaryContent = (
			<Typography
				data-testid="edit-btn"
				variant="body2"
				gutterBottom
				className={aisummaryStyles['summary-text-area']}
				onClick={handleTextClick}
			>
				{!isLoading ? (
					<Typography
						className={aisummaryStyles['summary-text']}
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(
								textData?.length && textData.replace(/\n/g, '<br />'),
							),
						}}
					/>
				) : (
					<Loader
						show={isLoading}
						className={aisummaryStyles['loader-styling']}
					/>
				)}
			</Typography>
		);
	}

	const isRegenerateDisabled =
		!data?.assignedUserId ||
		data?.assignedUserId !== loggedInUser?.userId ||
		isRegenerateLoading;

	const isDisabled =
		isRegenerateLoading ||
		(!(isEditing && !noChange) && !isRegenerating) ||
		!editData?.trim();
	const iconColor =
		isDisabled || isSaved ? disabledIconColor : activeIconColorPrimary;

	return (
		<div
			className={styles['section-container']}
			data-testid="section-container"
			ref={containerRef}
		>
			<Stack
				direction="row"
				justifyContent="space-between"
				className={aisummaryStyles['section-title']}
			>
				<Typography variant="h6" gutterBottom>
					AI summary
				</Typography>
				{isRegenerateVisible && (
					<div className={aisummaryStyles['regenerate-container']}>
						<PromptSelect
							aiModelKey={HORIZON_SCANNING_KEY}
							btnType="secondary"
							compClass={aisummaryStyles['regenerate-button']}
							startIcon={
								<Box className={aisummaryStyles['regenerate-button-icon']}>
									<Regenerate />
								</Box>
							}
							onSelectPrompt={generatePromptSelection}
							clickHandler={regenerateClicked}
							data-testid="regenerate-button-1"
							disabled={isRegenerateDisabled}
						>
							Regenerate
						</PromptSelect>
					</div>
				)}
			</Stack>
			<div>{AISummaryContent}</div>
			<div className={aisummaryStyles['button-container']}>
				<ButtonComp
					btnType="secondary"
					disabled={isDisabled || isSaved}
					startIcon={<SaveIcon fill={iconColor} />}
					compClass={aisummaryStyles['ai-summary-btn']}
					clickHandler={handleSaveClick}
				>
					Save
				</ButtonComp>
			</div>

			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</div>
	);
};

export default AISummary;
